// =============================================================================
// VandieZ admin-web -- User detail (/users/:userId)
// =============================================================================
// Shows one user's identity + counts + roles + effective permissions.
// Lets a super_admin:
//   * Suspend / unsuspend the account (with reason)
//   * Toggle which roles the user holds (replaces the role set in one PUT)
//
// Effective permissions are recomputed server-side after every role
// change and surfaced read-only so the operator sees the actual impact.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  ShieldOff,
  ShieldCheck,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { promotionsAdminApi, rolesAdminApi, usersAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import {
  BankVerificationModal,
  BankVerificationStatusPill,
} from "@/components/BankVerificationModal";
import { useToast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminUserDetail,
  Role,
  UserRoleAuditRow,
  UserTimelineEntry,
} from "@/types/rbac";
import type { AdminUserWalletResponse } from "@/types/admin_user_wallet";
import type { AdminUserTripsResponse } from "@/types/admin_user_trips";
import type { AdminUserDevicesResponse } from "@/types/admin_user_devices";
import type {
  AdminUserBankAccountRow,
  AdminUserBankAccountsResponse,
} from "@/types/admin_user_banks";
import type { AdminUserSmsResponse } from "@/types/admin_user_sms";
import type { AdminUserVehiclesResponse } from "@/types/admin_user_vehicles";
import type { AdminUserDisputesResponse } from "@/types/admin_user_disputes";

function statusPillClass(status: string): string {
  switch (status) {
    case "active":
      return "bg-success-subtle text-success-subtle-foreground";
    case "suspended":
    case "banned":
      return "bg-danger-subtle text-danger-subtle-foreground";
    case "pending_kyc":
      return "bg-warning-subtle text-warning-subtle-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function UserDetailPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const { accessToken, user: currentAdmin } = useAuth();
  const toast = useToast();

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [roleSelection, setRoleSelection] = useState<Set<string>>(new Set());
  const [savingRoles, setSavingRoles] = useState<boolean>(false);

  const [suspendReason, setSuspendReason] = useState<string>("");
  const [unsuspendReason, setUnsuspendReason] = useState<string>("");
  const [suspendBusy, setSuspendBusy] = useState<boolean>(false);
  const [forceLogoutBusy, setForceLogoutBusy] = useState<boolean>(false);

  const [audit, setAudit] = useState<UserRoleAuditRow[]>([]);
  const [timeline, setTimeline] = useState<UserTimelineEntry[]>([]);
  const [wallet, setWallet] = useState<AdminUserWalletResponse | null>(null);
  const [trips, setTrips] = useState<AdminUserTripsResponse | null>(null);
  const [devices, setDevices] = useState<AdminUserDevicesResponse | null>(
    null,
  );
  const [banks, setBanks] = useState<AdminUserBankAccountsResponse | null>(
    null,
  );
  const [smsHistory, setSmsHistory] = useState<AdminUserSmsResponse | null>(
    null,
  );
  const [vehicles, setVehicles] = useState<AdminUserVehiclesResponse | null>(
    null,
  );
  const [userDisputes, setUserDisputes] =
    useState<AdminUserDisputesResponse | null>(null);
  const [promotions, setPromotions] = useState<
    Awaited<ReturnType<typeof promotionsAdminApi.userPromotions>> | null
  >(null);
  const [adjustOpen, setAdjustOpen] = useState<boolean>(false);
  const [adjustDirection, setAdjustDirection] = useState<"credit" | "debit">(
    "credit",
  );
  const [adjustAmount, setAdjustAmount] = useState<string>("");
  const [adjustNote, setAdjustNote] = useState<string>("");
  const [adjustSubmitting, setAdjustSubmitting] = useState<boolean>(false);
  // Bank account currently open in the manual-verification modal (DR-24).
  const [reviewBank, setReviewBank] = useState<AdminUserBankAccountRow | null>(
    null,
  );

  const fetchAll = useCallback(
    async (signal?: AbortSignal) => {
      if (!accessToken || !userId) {return;}
      setLoading(true);
      setError(null);
      try {
        const [d, rs, ar, tl] = await Promise.all([
          usersAdminApi.detail(accessToken, userId, signal),
          rolesAdminApi.list(accessToken, signal),
          usersAdminApi.listRoleAudit(accessToken, userId, signal),
          usersAdminApi.timeline(accessToken, userId, { limit: 50 }, signal),
        ]);
        setDetail(d);
        setRoles(rs.roles);
        setRoleSelection(new Set(d.roles.map((r) => r.role_id)));
        setAudit(ar.items);
        setTimeline(tl.items);
        // Wallet is best-effort: failures don't block the rest of the page.
        void usersAdminApi
          .wallet(accessToken, userId, { limit: 10 }, signal)
          .then(setWallet)
          .catch(() => { /* card renders empty state */ });
        void usersAdminApi
          .trips(accessToken, userId, { limit: 15 }, signal)
          .then(setTrips)
          .catch(() => { /* card renders empty state */ });
        void usersAdminApi
          .devices(accessToken, userId, signal)
          .then(setDevices)
          .catch(() => { /* card stays hidden on error */ });
        void usersAdminApi
          .bankAccounts(accessToken, userId, signal)
          .then(setBanks)
          .catch(() => { /* card stays hidden on error */ });
        void usersAdminApi
          .smsHistory(accessToken, userId, { limit: 15 }, signal)
          .then(setSmsHistory)
          .catch(() => { /* card stays hidden on error */ });
        void usersAdminApi
          .vehicles(accessToken, userId, signal)
          .then(setVehicles)
          .catch(() => { /* card stays hidden on error */ });
        void usersAdminApi
          .disputes(accessToken, userId, { limit: 20 }, signal)
          .then(setUserDisputes)
          .catch(() => { /* card stays hidden on error */ });
        void promotionsAdminApi
          .userPromotions(accessToken, userId)
          .then(setPromotions)
          .catch(() => { /* card just doesn't render */ });
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // unmount
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, userId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchAll(c.signal);
    return () => { c.abort(); };
  }, [fetchAll]);

  // Refetch just the bank card after a verification decision — reloading
  // the whole page would throw away the roles/suspend form state below.
  const refreshBanks = useCallback((): void => {
    if (!accessToken || !userId) {return;}
    void usersAdminApi
      .bankAccounts(accessToken, userId)
      .then(setBanks)
      .catch(() => { /* keep the previous rows on a refresh failure */ });
  }, [accessToken, userId]);

  const toggleRole = (roleId: string): void => {
    setRoleSelection((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {next.delete(roleId);}
      else {next.add(roleId);}
      return next;
    });
  };

  const saveRoles = async (): Promise<void> => {
    if (!accessToken || !userId) {return;}
    setSavingRoles(true);
    setError(null);
    try {
      const res = await usersAdminApi.replaceRoles(accessToken, userId, {
        role_ids: Array.from(roleSelection),
      });
      // Refresh effective_permissions + role list on the detail card.
      setDetail((cur) =>
        cur === null
          ? cur
          : {
              ...cur,
              roles: res.roles,
              effective_permissions: res.effective_permissions,
            },
      );
      // Refetch the audit log so the brand-new grant/revoke row appears
      // immediately without a manual reload.
      try {
        const ar = await usersAdminApi.listRoleAudit(accessToken, userId);
        setAudit(ar.items);
      } catch {
        // Non-critical; the next page load will pick up the row.
      }
      toast.success("Roles updated.");
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
        toast.error(e.problem.detail);
      } else {
        setError("Failed to save roles.");
        toast.error("Failed to save roles.");
      }
    } finally {
      setSavingRoles(false);
    }
  };

  const suspend = async (): Promise<void> => {
    if (!accessToken || !userId) {return;}
    if (suspendReason.trim().length < 4) {
      setError("Suspension reason must be at least 4 characters.");
      return;
    }
    setSuspendBusy(true);
    setError(null);
    try {
      const res = await usersAdminApi.suspend(accessToken, userId, {
        reason: suspendReason.trim(),
      });
      setDetail(res);
      setSuspendReason("");
      toast.success(`Suspended ${res.full_name}.`);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
        toast.error(e.problem.detail);
      } else {
        setError("Failed to suspend.");
        toast.error("Failed to suspend.");
      }
    } finally {
      setSuspendBusy(false);
    }
  };

  const unsuspend = async (): Promise<void> => {
    if (!accessToken || !userId) {return;}
    // FSD p106 wants a reason on unblock as well as block. Checked here
    // as well as server-side so the admin gets the message next to the
    // field rather than as a 422 toast.
    if (unsuspendReason.trim().length < 4) {
      setError("Reason for restoring access must be at least 4 characters.");
      return;
    }
    setSuspendBusy(true);
    setError(null);
    try {
      const res = await usersAdminApi.unsuspend(accessToken, userId, {
        reason: unsuspendReason.trim(),
      });
      setDetail(res);
      setUnsuspendReason("");
      toast.success(`Unsuspended ${res.full_name}.`);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
        toast.error(e.problem.detail);
      } else {
        setError("Failed to unsuspend.");
        toast.error("Failed to unsuspend.");
      }
    } finally {
      setSuspendBusy(false);
    }
  };

  const forceLogout = async (): Promise<void> => {
    if (!accessToken || !userId || !detail) {return;}
    if (!window.confirm(
      `Force-logout ${detail.full_name}? This kicks them off every device. ` +
      "They can sign back in immediately afterwards unless you also suspend.",
    )) {return;}
    setForceLogoutBusy(true);
    setError(null);
    try {
      await usersAdminApi.forceLogout(accessToken, userId);
      toast.success(`Force-logged out ${detail.full_name}.`);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
        toast.error(e.problem.detail);
      } else {
        setError("Failed to force-logout.");
        toast.error("Failed to force-logout.");
      }
    } finally {
      setForceLogoutBusy(false);
    }
  };

  const isSelf = detail !== null && currentAdmin?.id === detail.id;
  const isSuspended = detail?.status === "suspended";
  const initiallyHeldRoleIds = new Set((detail?.roles ?? []).map((r) => r.role_id));
  const rolesChanged =
    detail !== null &&
    (roleSelection.size !== initiallyHeldRoleIds.size ||
      Array.from(roleSelection).some((id) => !initiallyHeldRoleIds.has(id)));

  return (
    <div className="space-y-4">
      <PageHeader
        title="User detail"
        description="Toggle roles, manage suspension, and inspect effective permissions for the user."
        actions={
          <Link
            to="/users"
            className="text-sm text-muted-foreground hover:underline flex items-center"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Users
          </Link>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading || detail === null ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-24 w-2/3" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{detail.full_name}</CardTitle>
                    <CardDescription>
                      <span className="font-mono">{detail.phone}</span>
                      {detail.email !== null && <span> · {detail.email}</span>}
                    </CardDescription>
                  </div>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusPillClass(detail.status)}`}
                  >
                    {detail.status.replace(/_/g, " ")}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                  <Field label="Primary role" value={detail.primary_role} />
                  <Field label="KYC tier" value={detail.kyc_tier} />
                  <Field label="Vehicles" value={String(detail.vehicle_count)} />
                  <Field label="Bank accounts" value={String(detail.bank_account_count)} />
                  <Field label="Trips" value={String(detail.trip_count)} />
                  <Field
                    label="Last seen"
                    value={
                      detail.last_login_at === null
                        ? "never"
                        : new Date(detail.last_login_at).toLocaleDateString()
                    }
                  />
                </div>
                {detail.suspended_at !== null && (
                  <Alert className="mt-4" variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Suspended at {new Date(detail.suspended_at).toLocaleString()}
                      {detail.suspended_reason !== null && (
                        <> · {detail.suspended_reason}</>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {wallet !== null && (
              <Card>
                <CardHeader>
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <CardTitle>Wallet</CardTitle>
                      <CardDescription>
                        Live balance + last {wallet.recent_entries.length} ledger
                        entries. Finance/super admins can issue a manual
                        adjustment.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "text-3xl font-semibold tabular-nums",
                          Number(wallet.balance) < 0
                            ? "text-danger-subtle-foreground"
                            : "text-success-subtle-foreground",
                        )}
                      >
                        ₹{Number(wallet.balance).toLocaleString("en-IN")}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setAdjustOpen(true); }}
                      >
                        Adjust
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {wallet.recent_entries.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      No ledger entries yet.
                    </div>
                  ) : (
                    <ul className="divide-y text-sm">
                      {wallet.recent_entries.map((e) => (
                        <li
                          key={e.id}
                          className="flex items-center justify-between gap-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">{e.entry_type}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(e.created_at).toLocaleString()}
                              {e.trip_id !== null && (
                                <Link
                                  to={`/trips/${e.trip_id}`}
                                  className="ml-2 underline"
                                >
                                  trip {e.trip_id.slice(0, 8)}
                                </Link>
                              )}
                            </div>
                          </div>
                          <div
                            className={cn(
                              "font-mono tabular-nums",
                              Number(e.amount) < 0
                                ? "text-danger-subtle-foreground"
                                : "text-success-subtle-foreground",
                            )}
                          >
                            {Number(e.amount) >= 0 ? "+" : ""}₹
                            {Number(e.amount).toLocaleString("en-IN")}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}

            {userDisputes !== null && userDisputes.rows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Disputes ({userDisputes.rows.length})
                  </CardTitle>
                  <CardDescription>
                    Disputes raised BY or AGAINST this user. The
                    "raised" pill marks self-raised vs counter-party.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {userDisputes.rows.map((d) => (
                      <li key={d.dispute_id} className="py-2">
                        <Link
                          to={`/disputes/${d.dispute_id}`}
                          className="flex items-center justify-between gap-3 hover:bg-muted/40 rounded px-2 py-1 -mx-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-xs uppercase tracking-wide",
                                  d.is_self_raised
                                    ? "bg-warning-subtle text-warning-subtle-foreground"
                                    : "bg-muted text-muted-foreground",
                                )}
                              >
                                {d.is_self_raised ? "raised" : "received"}
                              </span>
                              <span className="font-medium">{d.category}</span>
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-xs",
                                  d.status === "resolved"
                                    ? "bg-success-subtle text-success-subtle-foreground"
                                    : d.status === "rejected"
                                      ? "bg-danger-subtle text-danger-subtle-foreground"
                                      : "bg-info-subtle text-info-subtle-foreground",
                                )}
                              >
                                {d.status}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Trip {d.trip_id.slice(0, 8)} ·{" "}
                              {new Date(d.raised_at).toLocaleString()}
                              {d.resolution_action !== null && (
                                <> · {d.resolution_action}</>
                              )}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {promotions !== null && (
              <Card>
                <CardHeader>
                  <CardTitle>Promotions</CardTitle>
                  <CardDescription>
                    Referral code, referred-by chain, and awards earned.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm mb-2">
                    Referral code:{" "}
                    <span className="font-mono">{promotions.referral_code}</span>
                    {" · "}Referred {promotions.referred_count} users
                    {promotions.referred_by && (
                      <> · Referred by {promotions.referred_by.full_name}</>
                    )}
                  </div>
                  {promotions.awards.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      No awards yet.
                    </div>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {promotions.awards.map((a) => (
                        <li key={a.id} className="flex justify-between">
                          <span>
                            {a.program_code} · {a.trigger_summary}
                          </span>
                          <span>
                            ₹{a.amount} · {a.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}

            {smsHistory !== null && smsHistory.total > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    SMS history ({smsHistory.total})
                  </CardTitle>
                  <CardDescription>
                    Last {smsHistory.rows.length} SMS dispatches keyed
                    to this user. Newest first.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {smsHistory.rows.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs rounded bg-muted px-2 py-0.5">
                              {s.event_key}
                            </span>
                            <span
                              className={cn(
                                "rounded-full px-2 py-0.5 text-xs uppercase tracking-wide",
                                s.status === "delivered"
                                  ? "bg-success-subtle text-success-subtle-foreground"
                                  : s.status === "failed" ||
                                      s.status === "budget_exceeded"
                                    ? "bg-danger-subtle text-danger-subtle-foreground"
                                    : "bg-muted text-muted-foreground",
                              )}
                            >
                              {s.status}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {s.language} · {s.provider}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(s.sent_at).toLocaleString()}
                            {s.error_message !== null && (
                              <span className="ml-2 text-danger-subtle-foreground">
                                {s.error_message}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {vehicles !== null && vehicles.rows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Vehicles ({vehicles.rows.length})</CardTitle>
                  <CardDescription>
                    Trucks registered to this user. Click to open the
                    vehicle detail.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {vehicles.rows.map((v) => (
                      <li key={v.id} className="py-2">
                        <Link
                          to={`/vehicles/${v.id}`}
                          className="flex items-center justify-between gap-3 hover:bg-muted/40 rounded px-2 py-1 -mx-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium font-mono">
                              {v.registration_number}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {v.vehicle_type} ·{" "}
                              {new Date(v.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-xs uppercase",
                              v.status === "approved"
                                ? "bg-success-subtle text-success-subtle-foreground"
                                : v.status === "rejected" ||
                                    v.status === "deactivated"
                                  ? "bg-danger-subtle text-danger-subtle-foreground"
                                  : "bg-muted text-muted-foreground",
                            )}
                          >
                            {v.status}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {banks !== null && banks.rows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Bank accounts ({banks.rows.length})</CardTitle>
                  <CardDescription>
                    A payout is blocked until an account is verified, and the
                    only way there is a manual check — open Review and confirm
                    the details against a document. Account number masked to
                    last 4.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {banks.rows.map((b) => (
                      <li
                        key={b.id}
                        className="flex items-start justify-between gap-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{b.bank_name}</span>
                            {b.is_primary && (
                              <span className="rounded-full bg-primary-subtle text-primary-subtle-foreground px-2 py-0.5 text-xs">
                                primary
                              </span>
                            )}
                            <BankVerificationStatusPill
                              status={b.verification_status}
                            />
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {b.account_holder_name} · ****
                            {b.account_number_last_4} · IFSC {b.ifsc_code}
                            {b.branch_name !== null && (
                              <> · {b.branch_name}</>
                            )}
                          </div>
                          {b.upi_handle !== null && (
                            <div className="text-xs text-muted-foreground font-mono">
                              UPI: {b.upi_handle}
                            </div>
                          )}
                          {b.verified_at !== null && (
                            <div className="text-xs text-muted-foreground">
                              Ruled {new Date(b.verified_at).toLocaleString()}
                              {b.verification_basis !== null && (
                                <> · {b.verification_basis}</>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setReviewBank(b); }}
                        >
                          Review
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {devices !== null && devices.total_count > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Devices ({devices.active_count} active /{" "}
                    {devices.total_count} total)
                  </CardTitle>
                  <CardDescription>
                    FCM tokens registered against this user. Active first.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {devices.rows.map((d) => (
                      <li
                        key={d.id}
                        className={cn(
                          "flex items-center justify-between gap-3 py-2",
                          d.revoked_at !== null && "opacity-50",
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase tracking-wide">
                              {d.platform}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {d.device_token_prefix}…
                            </span>
                            {d.revoked_at !== null && (
                              <span className="text-xs text-danger-subtle-foreground">
                                revoked
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {d.device_model ?? "—"}
                            {d.app_version !== null && (
                              <> · v{d.app_version}</>
                            )}
                            <> · last used{" "}
                            {new Date(d.last_used_at).toLocaleString()}</>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {trips !== null && trips.rows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Recent trips ({trips.total})
                  </CardTitle>
                  <CardDescription>
                    The last {trips.rows.length} trips this user was on
                    (either side). Click to open the trip detail.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {trips.rows.map((t) => (
                      <li key={t.trip_id} className="py-2">
                        <Link
                          to={`/trips/${t.trip_id}`}
                          className="flex items-center justify-between gap-3 hover:bg-muted/40 rounded px-2 py-1 -mx-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                                {t.role}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {t.trip_id.slice(0, 8)}
                              </span>
                              <span className="text-xs rounded-full bg-muted px-2 py-0.5">
                                {t.status.replace(/_/g, " ")}
                              </span>
                            </div>
                            <div className="mt-0.5 truncate">
                              {t.pickup_district ?? "?"} →{" "}
                              {t.drop_district ?? "?"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(t.created_at).toLocaleString()}
                            </div>
                          </div>
                          {t.trip_value !== null && (
                            <div className="font-mono tabular-nums shrink-0">
                              ₹{Number(t.trip_value).toLocaleString("en-IN")}
                            </div>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Roles</CardTitle>
                <CardDescription>
                  Toggle roles to grant or revoke. Effective permissions
                  update after save.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {roles.map((role) => {
                    const selected = roleSelection.has(role.id);
                    return (
                      <label
                        key={role.id}
                        className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selected}
                          onChange={() => { toggleRole(role.id); }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2">
                            {role.name}
                            {role.is_system && (
                              <span className="text-xs uppercase tracking-wide rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                                system
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {role.description}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {role.permission_codes.length} permission{role.permission_codes.length === 1 ? "" : "s"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {detail.effective_permissions.length} effective permission
                    {detail.effective_permissions.length === 1 ? "" : "s"} currently.
                  </div>
                  <Button
                    onClick={() => void saveRoles()}
                    disabled={!rolesChanged || savingRoles}
                  >
                    {savingRoles ? "Saving…" : "Save roles"}
                  </Button>
                </div>

                {detail.effective_permissions.length > 0 && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Effective permissions ({detail.effective_permissions.length})
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {detail.effective_permissions.map((code) => (
                        <span
                          key={code}
                          className="rounded-full bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground"
                        >
                          {code}
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>

            {!isSelf && (
              <Card>
                <CardHeader>
                  <CardTitle>Suspension</CardTitle>
                  <CardDescription>
                    Suspended users can't authenticate. A reason is required
                    in BOTH directions and is stored on the audit row.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isSuspended ? (
                    <div className="space-y-3">
                      <Textarea
                        value={unsuspendReason}
                        onChange={(e) => {
                          setUnsuspendReason(e.target.value);
                        }}
                        placeholder="Why is access being restored? (stored on the audit row)…"
                        rows={3}
                      />
                      <Button
                        onClick={() => void unsuspend()}
                        disabled={
                          suspendBusy || unsuspendReason.trim().length < 4
                        }
                      >
                        <ShieldCheck className="mr-1 h-4 w-4" />
                        {suspendBusy ? "Working…" : "Unsuspend"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Textarea
                        value={suspendReason}
                        onChange={(e) => { setSuspendReason(e.target.value); }}
                        placeholder="Reason (visible on audit row + future login attempt)…"
                        rows={3}
                      />
                      <Button
                        variant="destructive"
                        onClick={() => void suspend()}
                        disabled={suspendBusy || suspendReason.trim().length < 4}
                      >
                        <ShieldOff className="mr-1 h-4 w-4" />
                        {suspendBusy ? "Working…" : "Suspend"}
                      </Button>
                    </div>
                  )}
                  <div className="pt-3 border-t">
                    <div className="text-xs text-muted-foreground mb-2">
                      Force-logout revokes every active session. Use when a
                      device is compromised or a refund/support call needs a
                      clean re-auth. Doesn't change account status.
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => void forceLogout()}
                      disabled={forceLogoutBusy}
                    >
                      {forceLogoutBusy ? "Working…" : "Force-logout all sessions"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isSelf && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You can't suspend yourself. Ask another super_admin if you
                  need to lock this account.
                </AlertDescription>
              </Alert>
            )}

            {/* Activity timeline — KYC + role + status audit rows
                unioned with the curated event_log entries for this user. */}
            <Card>
              <CardHeader>
                <CardTitle>Activity timeline ({timeline.length})</CardTitle>
                <CardDescription>
                  Recent events on this account. KYC decisions, role
                  grants, status flips, plus the events_log entries that
                  matter for support (login, signup, trip completion,
                  bid acceptance, etc.). Newest first.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No activity recorded yet.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {timeline.map((row, idx) => (
                      <li
                        key={`${row.timestamp}-${idx}`}
                        className="border rounded-md p-3 text-sm flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium flex items-center gap-2">
                            <span
                              className={
                                row.source === "audit_kyc"
                                  ? "rounded bg-success-subtle px-1.5 py-0.5 text-xs text-success-subtle-foreground"
                                  : row.source === "audit_role"
                                    ? "rounded bg-primary-subtle px-1.5 py-0.5 text-xs text-primary-subtle-foreground"
                                    : row.source === "audit_status"
                                      ? "rounded bg-warning-subtle px-1.5 py-0.5 text-xs text-warning-subtle-foreground"
                                      : "rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                              }
                            >
                              {row.source.replace(/_/g, " ")}
                            </span>
                            <span className="font-mono text-xs">
                              {row.label}
                            </span>
                          </div>
                          {(row.actor_name !== null || row.notes !== null) && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {row.actor_name !== null && (
                                <>by <span className="font-medium">{row.actor_name}</span></>
                              )}
                              {row.notes !== null && (
                                <span className="italic"> · {row.notes}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground text-right shrink-0">
                          {new Date(row.timestamp).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Role audit log */}
            <Card>
              <CardHeader>
                <CardTitle>Role audit log ({audit.length})</CardTitle>
                <CardDescription>
                  Every grant + revoke landing in audit.user_role_changes
                  for this user, newest first.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No role changes recorded yet.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {audit.map((row) => (
                      <li
                        key={row.id}
                        className="border rounded-md p-3 text-sm flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-medium">
                            <span
                              className={
                                row.action === "granted"
                                  ? "text-success-subtle-foreground"
                                  : "text-danger-subtle-foreground"
                              }
                            >
                              {row.action}
                            </span>{" "}
                            <span className="font-mono">
                              {row.role_name ?? row.role_id.slice(0, 8)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            by{" "}
                            <span className="font-medium">
                              {row.actor_name ??
                                (row.actor_user_id !== null
                                  ? row.actor_user_id.slice(0, 8)
                                  : "system")}
                            </span>
                            {row.notes !== null && (
                              <span className="italic"> · {row.notes}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground text-right shrink-0">
                          {new Date(row.changed_at).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}

      {adjustOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Adjust wallet</CardTitle>
              <CardDescription>
                Manual credit or debit. Logs as adjustment_credit /
                adjustment_debit ledger row. Note ≥ 10 chars required.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={adjustDirection === "credit" ? "default" : "outline"}
                  onClick={() => { setAdjustDirection("credit"); }}
                >
                  Credit (+)
                </Button>
                <Button
                  size="sm"
                  variant={adjustDirection === "debit" ? "default" : "outline"}
                  onClick={() => { setAdjustDirection("debit"); }}
                >
                  Debit (-)
                </Button>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={adjustAmount}
                  onChange={(e) => { setAdjustAmount(e.target.value); }}
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="100.00"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Reason (required, ≥ 10 chars)
                </label>
                <Textarea
                  value={adjustNote}
                  onChange={(e) => { setAdjustNote(e.target.value); }}
                  className="mt-1"
                  rows={3}
                  placeholder="e.g. Goodwill credit per ticket #1234 — driver had a stuck payout for 4 days."
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAdjustOpen(false);
                    setAdjustAmount("");
                    setAdjustNote("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={
                    adjustSubmitting ||
                    Number(adjustAmount) <= 0 ||
                    adjustNote.trim().length < 10
                  }
                  onClick={async () => {
                    if (accessToken === null || userId === undefined) {return;}
                    setAdjustSubmitting(true);
                    try {
                      const res = await usersAdminApi.adjustWallet(
                        accessToken,
                        userId,
                        {
                          direction: adjustDirection,
                          amount: adjustAmount,
                          note: adjustNote.trim(),
                        },
                      );
                      toast.success(
                        `Adjusted. New balance: ₹${Number(
                          res.new_balance,
                        ).toLocaleString("en-IN")}.`,
                      );
                      // Re-fetch wallet so the card reflects the new entry.
                      void usersAdminApi
                        .wallet(accessToken, userId, { limit: 10 })
                        .then(setWallet)
                        .catch(() => undefined);
                      setAdjustOpen(false);
                      setAdjustAmount("");
                      setAdjustNote("");
                    } catch (e: unknown) {
                      toast.error(
                        e instanceof ApiError
                          ? `Adjustment failed: ${e.problem.detail}`
                          : "Adjustment failed.",
                      );
                    } finally {
                      setAdjustSubmitting(false);
                    }
                  }}
                >
                  {adjustSubmitting ? "Posting…" : "Confirm"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {reviewBank !== null && userId !== undefined && (
        <BankVerificationModal
          userId={userId}
          account={reviewBank}
          onClose={() => { setReviewBank(null); }}
          onDecided={refreshBanks}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-medium capitalize">{value.replace(/_/g, " ")}</div>
    </div>
  );
}
